import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Box, Typography, TextField } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";
import useProject from "../../hooks/useProject";
import useBrowser from "../../hooks/useBrowser";

import EditorToolbar from "../editors/EditorToolbar";
import ListItem from "../elements/ListItem";
import Loading from "./Loading";

import TerritoryModal from "../modals/TerritoryModal";

const columns = [
  {
    name: "name",
    fieldType: "text",
  },
  {
    name: "repName",
    fieldType: "text",
  },
];

const Territories = ({
  syndicationId,
  projectId,
  onSave,
  onDelete,
  refresh,
  height,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { getFromServer, sortByFields } = useData();
  const { getMessage } = useLocalization();
  const { windowHeight } = useBrowser();

  const [openModal, setOpenModal] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [territories, setTerritories] = useState(null);
  const [filteredTerritories, setFilteredTerritories] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getData();
  }, [refresh]);

  useEffect(() => {
    if (!territories) {
      setFilteredTerritories(null);
      return;
    }

    if (!searchText.trim()) {
      setFilteredTerritories(territories);
      return;
    }

    const searchLower = searchText.toLowerCase();
    const filtered = territories.filter((territory) => {
      const territoryNameMatch = territory.name
        .toLowerCase()
        .includes(searchLower);
      const repNameMatch = territory.repName
        .toLowerCase()
        .includes(searchLower);
      return territoryNameMatch || repNameMatch;
    });

    setFilteredTerritories(filtered);
  }, [territories, searchText]);

  const getData = async () => {
    if (!projectId && !syndicationId) return;

    let filter = {};
    if (projectId) {
      filter.projectId = projectId;
    } else {
      filter.syndicationId = syndicationId;
    }

    let territoryData = await getFromServer({
      entityName: "Territory",
      pipeline: "withLocations",
      filter: filter,
    });

    console.log("[Territores].getData : territoryData ", territoryData);

    if (territoryData) {
      territoryData = sortByFields(territoryData, ["name"]);
      const transformed = territoryData.map((territory) => {
        const locationCount = territory?.locations.length || 0;
        const repName = territory.repName || "Unassigned";
        return {
          ...territory,
          name: `${territory.name} (${locationCount})`,
          repName: repName,
        };
      });
      setTerritories(transformed);
    } else {
      setTerritories([]);
    }
  };

  const handleAdd = () => {
    setOpenModal(true);
  };

  const handleTerritorySave = async () => {
    setOpenModal(false);
    onSave();
  };

  const handleTerritorySelect = (territory) => {
    if (!territory) return;

    navigate(`/territory`, {
      state: { territoryId: territory.id, projectId, syndicationId },
    });
  };

  const handleTerritoryDelete = (territory) => {
    onDelete(territory);
  };

  return (
    <>
      {territories === null ? (
        <Loading />
      ) : (
        <Box sx={{ height: "100%" }}>
          <EditorToolbar onAdd={handleAdd} />
          <Box sx={{ padding: "10px", paddingBottom: "5px" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search territories or reps..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ backgroundColor: "white" }}
            />
          </Box>
          <Box
            sx={{
              padding: "10px",
              paddingTop: "5px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              height: `${windowHeight - 275}px`,
              overflow: "auto",
            }}
          >
            {Array.isArray(filteredTerritories) &&
            filteredTerritories.length > 0 ? (
              filteredTerritories.map((territory) => (
                <ListItem
                  key={territory.id}
                  record={territory}
                  columns={columns}
                  onClick={() => handleTerritorySelect(territory)}
                  fontStyle={{ fontSize: "12px" }}
                />
              ))
            ) : (
              <Typography>
                {searchText.trim()
                  ? "No territories match your search"
                  : getMessage("noRecords")}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <TerritoryModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleTerritorySave}
        projectId={projectId}
        syndicationId={syndicationId}
        territoryId={selectedTerritory}
        onDelete={handleTerritoryDelete}
        territories={territories}
      />
    </>
  );
};

export default Territories;
